/**
 * 管理后台 API 客户端(axios + JWT 拦截 + 错误码处理)
 * 演示:使用 @nutrition-talent/shared-types 错误码枚举
 */

import axios, { type AxiosInstance, AxiosError } from 'axios';
import { ErrorCode, type ApiErrorResponse, type ApiSuccessResponse } from '@nutrition-talent/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://t-api.yyjkrcw.com';

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

/**
 * BUG #32 修复:登录态存在 localStorage['nt_admin_auth'](JSON 串)
 * 内含 accessToken 字段,不能用旧的 'access_token' 平铺 key
 */
const ADMIN_AUTH_STORAGE_KEY = 'nt_admin_auth';

function readAdminAccessToken(): string | null {
  const raw = localStorage.getItem(ADMIN_AUTH_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.accessToken ?? null;
  } catch {
    return null;
  }
}

// 请求拦截:自动加 Bearer token
http.interceptors.request.use((config) => {
  const token = readAdminAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截:统一错误处理
http.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    if (error.response?.data?.code) {
      const errCode = error.response.data.code as ErrorCode;
      switch (errCode) {
        case ErrorCode.AUTH_TOKEN_EXPIRED:
        case ErrorCode.AUTH_TOKEN_INVALID:
          localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
          window.location.href = '/login';
          break;
        case ErrorCode.PLATFORM_ONLY:
        case ErrorCode.ASSOCIATION_FORBIDDEN:
          // eslint-disable-next-line no-console
          console.error('[权限错误]', error.response.data.message);
          break;
      }
    }
    return Promise.reject(error);
  },
);

/**
 * 从任意错误对象提取后端业务 message
 * 优先取 axios response.data.message(后端 ApiError 友好文案),
 * fallback 到 err.message(axios/network/JS 错误),最后 fallback 到 defaultMsg。
 *
 * 用法:
 *   } catch (err) {
 *     ElMessage.error(getErrorMessage(err, '操作失败'));
 *   }
 */
export function getErrorMessage(err: unknown, defaultMsg = '操作失败'): string {
  if (axios.isAxiosError<ApiErrorResponse>(err)) {
    const backendMsg = err.response?.data?.message;
    if (backendMsg) return backendMsg;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return defaultMsg;
}

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await http.get<T>(url, { params });
  return res.data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const res = await http.post<T>(url, body);
  return res.data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const res = await http.put<T>(url, body);
  return res.data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const res = await http.patch<T>(url, body);
  return res.data;
}

export async function del<T>(url: string): Promise<T> {
  const res = await http.delete<T>(url);
  return res.data;
}
